import styled, { css, keyframes } from 'styled-components';
import { motion } from 'motion/react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const base = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.radii.full};
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition:
    background-color ${({ theme }) => theme.durations.fast}s ease,
    border-color ${({ theme }) => theme.durations.fast}s ease,
    color ${({ theme }) => theme.durations.fast}s ease,
    box-shadow ${({ theme }) => theme.durations.fast}s ease,
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);

  /* Remove outline nativo — focus-visible cuida do a11y. */
  outline: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.semantic.brand.accent};
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }
`;

const sizes: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    height: 36px;
    padding: 0 ${({ theme }) => theme.spacing[4]};
    font-size: ${({ theme }) => theme.fontSizes.sm};
  `,
  md: css`
    height: 48px;
    padding: 0 ${({ theme }) => theme.spacing[6]};
    font-size: ${({ theme }) => theme.fontSizes.base};
  `,
  lg: css`
    height: 58px;
    padding: 0 ${({ theme }) => theme.spacing[8]};
    font-size: ${({ theme }) => theme.fontSizes.lg};
  `,
};

const variants: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background-color: ${({ theme }) => theme.semantic.brand.accent};
    color: ${({ theme }) => theme.colors.navy[900]};
    border-color: transparent;
    box-shadow: ${({ theme }) => theme.shadows.sm};

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) => theme.colors.cyan[300]};
        box-shadow: ${({ theme }) => theme.shadows.glow};
      }
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.semantic.brand.primary};
    color: ${({ theme }) => theme.semantic.text.inverse};
    border-color: transparent;
    box-shadow: ${({ theme }) => theme.shadows.sm};

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) => theme.semantic.brand.primaryHover};
        box-shadow: ${({ theme }) => theme.shadows.md};
      }
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.semantic.text.primary};
    border-color: transparent;

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) => theme.semantic.background.secondary};
      }
    }
  `,
  outline: css`
    background-color: transparent;
    color: ${({ theme }) => theme.semantic.brand.accent};
    border-color: ${({ theme }) => theme.semantic.brand.accent};

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) => theme.semantic.brand.accent};
        color: ${({ theme }) => theme.colors.navy[900]};
        box-shadow: ${({ theme }) => theme.shadows.glow};
      }
    }
  `,
};

/** Variante inverse — para uso em fundos escuros navy. */
export const inverseVariants: Partial<Record<ButtonVariant, ReturnType<typeof css>>> = {
  ghost: css`
    color: ${({ theme }) => theme.semantic.text.inverse};

    @media (hover: hover) {
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  `,
  outline: css`
    color: ${({ theme }) => theme.semantic.text.inverse};
    border-color: rgba(255, 255, 255, 0.4);

    @media (hover: hover) {
      &:hover {
        background-color: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.8);
      }
    }
  `,
};

export const StyledButton = styled(motion.button)<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $inverse?: boolean;
  $fullWidth?: boolean;
}>`
  ${base}
  ${({ $size }) => sizes[$size]}
  ${({ $variant }) => variants[$variant]}
  ${({ $variant, $inverse }) => $inverse && (inverseVariants[$variant] ?? '')}
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0px);
    }
  }
`;

export const SpinnerIcon = styled.span`
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export const ButtonContent = styled.span<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: inherit;
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  transition: opacity ${({ theme }) => theme.durations.fast}s;
`;

export const LoadingOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
